import { Fetch } from "helpers/fetchWrapper";
import { useEffect, useState, useCallback } from "react";
import { useCancellablePromise } from "helpers/promiseHandler";

const STATUS = {
    LOADING: "loading",
    SUCCESS: "success",
    ERROR: "error",
};

const reviewService = {
    _url: `${process.env.REACT_APP_REVIEWS_RATING_URL}`,

    // ──────────────────────────────────────────────
    // Hook: Paginated reviews for a business
    // ──────────────────────────────────────────────
    GetBusinessReviews(searchQuery, refetch) {
        const [reviews, setReviews] = useState({
            reviews: [],
            totalItems: 0,
            totalPages: 1,
            currentPage: 1,
        });
        const { cancellablePromise } = useCancellablePromise();
        const [status, setStatus] = useState(STATUS.LOADING);

        useEffect(() => {
            if (!searchQuery?.restaurantId) return;
            setStatus(STATUS.LOADING);
            cancellablePromise(this.getBusinessReviews(searchQuery))
                .then(res => {
                    setReviews(() => res?.reviewsData || {
                        reviews: [],
                        totalItems: 0,
                        totalPages: 1,
                        currentPage: 1,
                    });
                    setStatus(STATUS.SUCCESS);
                })
                .catch(() => setStatus(STATUS.ERROR));
        }, [JSON.stringify(searchQuery), refetch]);

        return {
            reviews_loading: status === STATUS.LOADING,
            reviews_error: status === STATUS.ERROR ? status : "",
            reviews_data: reviews,
        };
    },

    // ──────────────────────────────────────────────
    // Hook: Review stats + top reviews for slider
    // ──────────────────────────────────────────────
    GetBusinessReviewStats(restaurantId, storeId, refetch) {
        const [stats, setStats] = useState({
            totalReviews: 0,
            avgFoodRating: 0,
            avgDriverRating: 0,
            avgOverallRating: 0,
            complaints: 0,
            breakdown: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 },
        });
        const [highlights, setHighlights] = useState({
            topRatedItem: null,
            lowestRatedItem: null,
            mostComplaintsItem: null,
        });
        const [topReviews, setTopReviews] = useState([]);
        const { cancellablePromise } = useCancellablePromise();
        const [status, setStatus] = useState(STATUS.LOADING);

        useEffect(() => {
            if (!restaurantId) return;
            setStatus(STATUS.LOADING);
            cancellablePromise(this.getBusinessReviewStats(restaurantId, storeId))
                .then(res => {
                    setStats(res?.stats || {});
                    setHighlights(res?.highlights || {});
                    setTopReviews(res?.topReviews || []);
                    setStatus(STATUS.SUCCESS);
                })
                .catch(() => setStatus(STATUS.ERROR));
        }, [restaurantId, storeId, refetch]);

        return {
            stats_loading: status === STATUS.LOADING,
            stats_error: status === STATUS.ERROR ? status : "",
            stats_data: stats,
            highlights: highlights,
            top_reviews: topReviews,
        };
    },

    // ──────────────────────────────────────────────
    // API: Fetch paginated reviews for a business
    // ──────────────────────────────────────────────
    async getBusinessReviews({
        restaurantId = "",
        page = 1,
        itemsPerPage = 10,
        searchText = "",
        startDate = "",
        endDate = "",
        storeId = "",
    }) {
        const params = new URLSearchParams();
        params.append("page", page);
        params.append("itemsPerPage", itemsPerPage);
        if (searchText) params.append("searchText", searchText);
        if (startDate) params.append("startDate", startDate);
        if (endDate) params.append("endDate", endDate);
        if (storeId) params.append("storeId", storeId);

        const res = await Fetch.get(
            `${this._url}/getBusinessReviews/${restaurantId}?${params.toString()}`
        );
        if (res.status >= 200 && res.status < 300) {
            return await res.json();
        }
        const { message } = await res.json().catch(() => ({}));
        throw new Error(message ?? "Failed to fetch reviews");
    },

    // ──────────────────────────────────────────────
    // API: Fetch review stats + top reviews for slider
    // ──────────────────────────────────────────────
    async getBusinessReviewStats(restaurantId, storeId) {
        let url = `${this._url}/getBusinessReviewStats/${restaurantId}`;
        if (storeId) {
            url += `?storeId=${storeId}`;
        }
        const res = await Fetch.get(url);
        if (res.status >= 200 && res.status < 300) {
            return await res.json();
        }
        const { message } = await res.json().catch(() => ({}));
        throw new Error(message ?? "Failed to fetch review stats");
    },

    // ──────────────────────────────────────────────
    // API: Reply to a review
    // ──────────────────────────────────────────────
    async replyToReview(reviewId, replyText) {
        const res = await Fetch.post(
            `${this._url}/replyToReview/${reviewId}`,
            { replyText }
        );
        if (res.status >= 200 && res.status < 300) {
            return await res.json();
        }
        const { message } = await res.json().catch(() => ({}));
        throw new Error(message ?? "Failed to submit reply");
    },
};

export default reviewService;
